import { debounce, Slider, Stack, SxProps, Tooltip, Typography, useTheme } from "@mui/material";
import { FC, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  GetFrameRate,
  GetFullScreen,
  GetIsFileTCDisplayed,
  GetRealCurrentFrame,
  GetTimecodeIn,
  GetTotalFrameCount,
  GetVttThumbFromTime,
  SetSeek,
} from "../../../Redux/Reducers/redux-slice-react-player";
import { FrameToTimecode, TimecodeToFileTimecode } from "../../../Utils/Timecode/ReactPlayer";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import { GetObject } from "../../../Redux/Reducers/redux-slice-asset";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { PreviewContext } from "../../../Pages/admin/Inventory/InventoryAssetSingle/AssetSinglePreview/asset-single-preview";
import { Theme } from "@mui/material/styles";
import { GetPanelObject } from "../../../Redux/Reducers/redux-slice-drive";
import { useHover } from "@uidotdev/usehooks";

interface IPlayerProgressProps {
  type: ProgressBarType;
  context: PreviewContext;
}

export enum ProgressBarType {
  Player,
  Marker,
}

export const PlayerProgress: FC<IPlayerProgressProps> = (props) => {
  const { type, context } = props;

  const totalFrameCount = useAppSelector(GetTotalFrameCount);
  const progress = useAppSelector(GetRealCurrentFrame);
  const [progressLocal, setProgressLocal] = useState(progress);
  const frameRate = useAppSelector(GetFrameRate);
  const isPlayerFullScreen = useAppSelector(GetFullScreen);
  const isFileTCDisplayed = useAppSelector(GetIsFileTCDisplayed);
  const timecodeIn = useAppSelector(GetTimecodeIn);
  const [mouseFrame, setMouseFrame] = useState<number>(0);
  const sliderRef = useRef<HTMLSpanElement>(null);
  const objDrive = useAppSelector(GetPanelObject);
  const objAsset = useAppSelector(GetObject);
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const [isDragging, setIsDragging] = useState(false);

  const [playerProgress, hoveringProgress] = useHover();

  const obj = useMemo(() => {
    return context !== PreviewContext.DriveEnlarged && context !== PreviewContext.DriveFolderPreview
      ? objAsset
      : objDrive;
  }, [context]);

  useEffect(() => {
    if (!isDragging) {
      setProgressLocal(progress);
    }
  }, [progress]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (sliderRef.current) {
      const frame = Math.min(
        Math.max(
          ((e.clientX - sliderRef.current.getBoundingClientRect().x) /
            sliderRef.current.getBoundingClientRect().width) *
            totalFrameCount,
          0
        ),
        totalFrameCount
      );
      setMouseFrame(frame);
    }
  }, []);

  const SliderSx: SxProps<Theme> = useMemo(
    () => ({
      ...(type === ProgressBarType.Player && {
        height: hoveringProgress ? 8 : 2,
        color: theme.palette.primary.main,
        "& .MuiSlider-thumb": {
          display: hoveringProgress ? "block" : "none",
          width: 24,
          height: 24,
          // transition: "0.15s cubic-bezier(.28,.01,.76,1)",
          "&::before": {
            boxShadow: "0 2px 12px 0 rgba(0,0,0,0.4)",
          },
          "&:hover, &.Mui-focusVisible": {
            boxShadow: `0px 0px 0px 8px ${"rgb(0 0 0 / 16%)"}`,
            ...theme.applyStyles("dark", {
              boxShadow: `0px 0px 0px 8px ${"rgb(255 255 255 / 16%)"}`,
            }),
          },
          "&.Mui-active": {
            width: 20,
            height: 20,
          },
        },
        "& .MuiSlider-rail": {
          opacity: 1,
          background: theme.palette.background[400],
          ...(isPlayerFullScreen && {
            borderRadius: 1,
          }),
        },
        "& .MuiSlider-track": {
          height: hoveringProgress ? 8 : 2,
          ...(isPlayerFullScreen && {
            borderTopLeftRadius: "6px",
            borderBottomLeftRadius: "6px",
          }),
        },
      }),
      ...(type === ProgressBarType.Marker && {
        height: 16,
        "& .MuiSlider-rail": {
          opacity: 1,
          background: theme.palette.background[400],
        },
        "& .MuiSlider-thumb": {
          width: 20,
          height: 20,
          position: "relative",
          zIndex: 2,
          top: -16,
          color: theme.palette.absolute.white,
          "&::before": {
            boxShadow: "0 2px 12px 0 rgba(0,0,0,0.4)",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            top: 30,
            height: 40,
            width: "1px",
            background: theme.palette.text.primary,
            opacity: 0.5,
          },
          "&:hover, &.Mui-focusVisible": {
            boxShadow: `0px 0px 0px 8px ${"rgb(0 0 0 / 16%)"}`,
            ...theme.applyStyles("dark", {
              boxShadow: `0px 0px 0px 8px ${"rgb(255 255 255 / 16%)"}`,
            }),
          },
          "&.Mui-active": {
            width: 16,
            height: 16,
          },
        },
        "& .MuiSlider-track": {
          height: 16,
          background: theme.palette.background.alphaHover,
        },
      }),
      padding: "0px !important",
      borderRadius: 0,
    }),
    [theme, type, hoveringProgress]
  );

  // value can't be an array since we are not using a range slider
  // https://mui.com/material-ui/react-slider/#range-slider
  const handleTimeChange = useCallback((_, newValue: number | number[]) => {
    setProgressLocal(newValue as number);
    dispatchTimeChange(newValue as number);
  }, []);

  const dispatchTimeChange = debounce((newValue: number) => {
    dispatch(SetSeek(newValue));
  }, 100);

  const handleDragStart = () => {
    setIsDragging(true);
  };

  // TO is here to ensure the progressLocal doesn't get flooded by progress while the data flows in and out of Redux
  const handleDragEnd = () => {
    setTimeout(() => {
      setIsDragging(false);
    }, 200);
  };

  return (
    <>
      <Tooltip
        followCursor
        slotProps={{
          tooltip: {
            sx: {
              padding: 0,
              border: "none",
              ...theme.helpers(theme).popoverMenuHighlights,
            },
          },
          popper: {
            container: sliderRef.current,
          },
        }}
        title={
          <Stack p={0}>
            {obj && obj.type === ObjectType.Video && <ProgressThumb mouseTime={mouseFrame} context={context} />}
            <Typography variant="subtitle2" sx={{ padding: 1, textAlign: "center" }}>
              {isFileTCDisplayed
                ? TimecodeToFileTimecode(timecodeIn, FrameToTimecode(mouseFrame, frameRate), frameRate)
                : FrameToTimecode(mouseFrame, frameRate)}
            </Typography>
          </Stack>
        }
        placement={"top"}
      >
        <Stack
          ref={playerProgress}
          sx={{ width: "100%", height: 2, justifyContent: "flex-end", px: isPlayerFullScreen ? 1 : 0 }}
        >
          <Slider
            aria-label="time-indicator"
            size="small"
            value={progressLocal}
            min={0}
            step={frameRate}
            max={totalFrameCount}
            onMouseMove={handleMouseMove}
            onChange={handleTimeChange}
            onMouseDown={handleDragStart}
            onMouseUp={handleDragEnd}
            onTouchStart={handleDragStart}
            onTouchEnd={handleDragEnd}
            ref={sliderRef}
            sx={SliderSx}
          />
        </Stack>
      </Tooltip>
    </>
  );
};

export const ProgressThumb = (props: { mouseTime: number; context: PreviewContext }) => {
  const { mouseTime, context } = props;
  const fullScreen = useAppSelector(GetFullScreen);
  const currentVttThumb = useAppSelector(GetVttThumbFromTime(mouseTime));

  const thumbWidth = useMemo(() => {
    if (fullScreen) return 300;
    else if (
      context === PreviewContext.DistribEnlarged ||
      context === PreviewContext.DriveEnlarged ||
      context === PreviewContext.DistribEnlargedTabs
    ) {
      return 250;
    } else {
      return 120;
    }
  }, [context, fullScreen]);

  if (!currentVttThumb) return <></>;

  return (
    <figure
      style={{
        width: thumbWidth,
        aspectRatio: "16 / 9",
        overflow: "hidden",
        borderRadius: "4px 4px 0 0",
        backgroundImage: `url(${currentVttThumb.text})`,
        backgroundSize: `cover`,
      }}
    />
  );
};
